import React from "react";
import { motion } from "framer-motion";
import VideoEmbed from "../VideoEmbed";

const reasons = [
  {
    num: "01",
    title: "Enterprise-grade experience.",
    desc: "Fortune 1000s and high-growth challengers — across industries, regions, and cycles.",
  },
  {
    num: "02",
    title: "Precision at scale, with AI.",
    desc: "Proprietary + 3rd-party stacks combined with AI workflows — full-funnel, at scale.",
  },
  {
    num: "03",
    title: "Insight-driven strategy.",
    desc: "Data science meets human intelligence to uncover what truly drives B2B decisions.",
  },
];

export default function WhyChooseUs() {
  return (
    <section
      id="why"
      data-testid="why-choose-us"
      className="section-dark py-section relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/3 -right-40 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.22),transparent_60%)] blur-3xl" />
        <div className="absolute -top-40 -left-20 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(76,125,255,0.22),transparent_60%)] blur-3xl" />
      </div>
      <div className="grain-overlay" />

      <div className="container-xl relative">
        <div className="max-w-4xl mb-12 md:mb-20">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FFB000] mb-5">
            Why B2B leaders choose dotConverse
          </div>
          <h2 className="font-display text-white text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-semibold">
            Relevance. Timing.{" "}
            <span className="font-serif italic font-light text-white/60">
              Trust.
            </span>
          </h2>
          <p className="mt-6 text-white/65 text-lg max-w-2xl leading-relaxed">
            B2B success isn&apos;t just about reach. It&apos;s about engaging
            the right decision-makers, accelerating pipeline movement, and
            staying top-of-mind across every stage of the sale.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* Video */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <VideoEmbed
              videoId="V3BHKAyJzoA"
              title="Built for modern B2B growth"
              testId="why-us-video"
              theme="dark"
            />
          </motion.div>

          {/* Reasons */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            {reasons.map((r, i) => (
              <motion.div
                key={r.num}
                data-testid={`why-us-reason-${i}`}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.8,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group relative rounded-[24px] bg-white/[0.04] backdrop-blur-md border border-white/10 p-6 md:p-7 hover:bg-white/[0.07] transition-colors"
              >
                <div className="flex items-start gap-6">
                  <div className="font-display text-[#FFB000] text-3xl md:text-4xl tracking-tight font-semibold shrink-0">
                    {r.num}
                  </div>
                  <div>
                    <h3 className="font-display text-white text-xl md:text-2xl tracking-tight leading-tight font-medium mb-2">
                      {r.title}
                    </h3>
                    <p className="text-white/65 text-[15px] leading-relaxed">
                      {r.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
